/**
 * PipelineSettings — editor de etapas do funil (diferencial F1).
 *
 * CRUD de stages: adicionar, renomear, reordenar (drag), cor, marcar
 * ganho/perdido, excluir (soft). Consome /api/crm/stages. Backend recalcula
 * stage_count. Ao fechar, o board recarrega.
 *
 * Visual: mesmo padrão de modal centrado do módulo Financeiro (fc-modal-*,
 * ../financeiro/contas.css) + swatch de cor do Pipelines (pm-color-swatch).
 */

import { useEffect, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { getStages, updateStage, deleteStage } from '../../db/api.js'
import StageCreateModal from './StageCreateModal.jsx'
import '../financeiro/contas.css'
import './pipelines.css'

export default function PipelineSettings({ pipelineId, pipelineNome, onClose, onSaved, tabs = null }) {
  const [stages, setStages] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)
  const [addOpen, setAddOpen] = useState(false)   // modal criar etapa
  const [editId, setEditId] = useState(null)       // etapa em edição de nome
  const [editVal, setEditVal] = useState('')

  const load = async () => {
    try {
      setStages(await getStages(pipelineId))
    } catch (e) { setErr(e.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [pipelineId]) // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (id, data) => {
    setErr(null)
    try { await updateStage(id, data); setDirty(true); await load() }
    catch (e) { setErr(e.message) }
  }

  // paleta padrão — dá uma cor inicial distinta a cada etapa nova (editável).
  const PALETTE = ['#0071e3', '#34a853', '#ff9f0a', '#a259ff', '#e85d04', '#ff3b30', '#00b8a3', '#6e6e73']

  // Criação de etapa via modal (StageCreateModal) — sem window.prompt.
  const handleAdd = () => setAddOpen(true)
  const handleAdded = async () => { setAddOpen(false); setDirty(true); await load() }

  // Edição de nome inline: pencil abre input; salva no Enter/blur, cancela no Esc.
  const startEdit = (s) => { setEditId(s.id); setEditVal(s.nome) }
  const cancelEdit = () => { setEditId(null); setEditVal('') }
  const commitEdit = (s) => {
    const nome = editVal.trim()
    if (nome && nome !== s.nome) patch(s.id, { nome })
    cancelEdit()
  }

  const handleDelete = async (s) => {
    if (!window.confirm(`Excluir a etapa "${s.nome}"?`)) return
    setErr(null)
    try { await deleteStage(s.id); setDirty(true); await load() }
    catch (e) { setErr(e.message) }
  }

  // Reordena via drag-and-drop: move a etapa arrastada para a posição-alvo e
  // persiste a nova ordem (ordem = índice) de todas as etapas afetadas.
  const handleDropReorder = async (to) => {
    const from = dragIdx
    setDragIdx(null); setOverIdx(null)
    if (from == null || from === to) return
    const next = [...stages]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setStages(next)                       // otimista
    try {
      await Promise.all(
        next.map((s, i) => (s.ordem !== i ? updateStage(s.id, { ordem: i }) : null)).filter(Boolean)
      )
      setDirty(true); await load()
    } catch (e) {
      setErr(e.message); await load()     // reverte
    }
  }

  const close = () => (dirty ? onSaved() : onClose())

  return (
    <>
      <div className="fc-modal-overlay" onMouseDown={close}>
        <div className="fc-modal" style={{ width: tabs ? 720 : 600, maxWidth: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
          <div className="fc-modal-head">
            <span className="fc-drawer-title">{tabs ? 'Configurações do pipeline' : `Etapas — ${pipelineNome}`}</span>
            <button className="fc-drawer-x" onClick={close}><Icon n="x" s={16} sw={2} /></button>
          </div>
          {tabs}
          <div className="fc-modal-body">
            {err && <div className="fc-error">{err}</div>}
            {loading ? (
              <div style={{ padding: '20px 0', color: '#667085', fontSize: 14 }}>Carregando…</div>
            ) : (
              <>
                {!stages.length && <div style={{ padding: '20px 0', color: '#667085', fontSize: 14 }}>Nenhuma etapa. Adicione a primeira.</div>}
                {stages.map((s, i) => (
                  <div
                    key={s.id}
                    className="pm-list-row"
                    style={{
                      cursor: 'grab',
                      ...(overIdx === i && dragIdx !== null && dragIdx !== i
                        ? { boxShadow: 'inset 0 2px 0 #2563eb', background: '#f7faff' } : null),
                      ...(dragIdx === i ? { opacity: .5 } : null),
                    }}
                    draggable
                    onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move' }}
                    onDragOver={(e) => { e.preventDefault(); setOverIdx(i) }}
                    onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
                    onDrop={() => handleDropReorder(i)}
                    onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}
                  >
                    <span style={{ display: 'inline-flex', color: '#98a2b3', flexShrink: 0 }} title="Arraste para reordenar">
                      <Icon n="grip" s={14} />
                    </span>

                    <label className="pm-color-swatch" style={{ width: 24, height: 24 }} title="Alterar cor da etapa">
                      <input
                        type="color"
                        value={s.cor || '#888888'}
                        onChange={(e) => patch(s.id, { cor: e.target.value })}
                      />
                      <span className="pm-color-swatch-dot" style={{ borderRadius: '50%', background: s.cor || '#888888' }} />
                    </label>

                    {editId === s.id ? (
                      <input
                        autoFocus
                        className="fc-input"
                        style={{ flex: 1, height: 32 }}
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        onBlur={() => commitEdit(s)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitEdit(s) }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
                        }}
                        aria-label="Nome da etapa"
                      />
                    ) : (
                      <button
                        type="button"
                        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', border: 'none', background: 'none', cursor: 'text', padding: 0 }}
                        onClick={() => startEdit(s)}
                        title="Clique para renomear"
                      >
                        <span className="pm-list-name" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.nome}</span>
                        <Icon n="edit" s={13} c="#98a2b3" />
                      </button>
                    )}

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#667085', flexShrink: 0 }} title="Probabilidade de fechamento nesta etapa">
                      <input
                        type="number" min="0" max="100" step="1"
                        className="fc-input"
                        style={{ width: 52, height: 26, padding: '0 4px', textAlign: 'right' }}
                        value={s.probabilidade ?? ''}
                        placeholder="—"
                        onChange={(e) => {
                          const v = e.target.value
                          patch(s.id, { probabilidade: v === '' ? null : Number(v) })
                        }}
                      /> %
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#667085', flexShrink: 0 }}>
                      <input type="checkbox" checked={!!s.is_ganho}
                        onChange={(e) => patch(s.id, { is_ganho: e.target.checked })} /> ganho
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#667085', flexShrink: 0 }}>
                      <input type="checkbox" checked={!!s.is_perdido}
                        onChange={(e) => patch(s.id, { is_perdido: e.target.checked })} /> perdido
                    </label>
                    <button className="pm-list-removebtn" onClick={() => handleDelete(s)}>excluir</button>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="fc-modal-foot" style={{ justifyContent: 'space-between' }}>
            <button className="fc-ctrl" onClick={handleAdd}>
              <Icon n="plus" s={13} sw={2} /> Adicionar etapa
            </button>
            <button className="fc-ctrl primary" onClick={close}>Concluir</button>
          </div>
        </div>
      </div>
      {addOpen && (
        <StageCreateModal
          pipelineId={pipelineId}
          ordem={stages.length}
          cor={PALETTE[stages.length % PALETTE.length]}
          onClose={() => setAddOpen(false)}
          onCreated={handleAdded}
        />
      )}
    </>
  )
}
